'use client';

import React from 'react';
import { useSearchParams } from 'next/navigation';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useTekstClient } from 'hooks/useTekstClient';
import styles from './AddOnCategoryBadges.module.scss';

const HIDDEN_KEYS = new Set(['status', 'talen', 'landen', 'aanbieders']);
const normalizeNameSlug = (name) => name?.toLowerCase().replace(/[^a-z]/g, '');

/**
 * Renders category badges for an add-on. Only shows MKG-modules categories.
 */
export default function AddOnCategoryBadges({ addOn, filters = [] }) {
    const { tekst } = useTekstClient();
    const searchParams = useSearchParams();
    const { doMultipleChanges } = useSetSearchParams();
    const categoryMeta = new Map();

    filters.forEach((f) => {
        const slug = f.slug ?? 'filter_' + f.id;
        const modulesFilter = Boolean(f?.is_mkg_modules);
        (f.subfilters || []).forEach((s) => {
            if (s?.id) {
                categoryMeta.set(s.id, {
                    slug,
                    key: f.key ?? null,
                    name: s.naam,
                    variant: s.variant ?? null,
                    isMkgModules: modulesFilter,
                });
            }
        });
    });

    const getCategoryName = (c) =>
        categoryMeta.get(c.id)?.name ??
        c?.naam ??
        c?.categorie_talen_in_taal?.[0]?.categorie_taal?.naam ??
        c?.categorie_talen_in_taal?.[0]?.categorieTaal?.naam;

    const getSelectedForSlug = (slug) => {
        const param = searchParams.get(slug);
        if (!param?.trim()) return new Set();
        return new Set(
            param
                .split(',')
                .map((x) => parseInt(x, 10))
                .filter((n) => !isNaN(n))
        );
    };

    const getBadges = () => {
        const categorieen = addOn?.categorieen;
        if (!Array.isArray(categorieen)) return [];

        return categorieen
            .filter((c) => {
                if (!c?.id || !categoryMeta.has(c.id)) return false;
                return categoryMeta.get(c.id)?.isMkgModules === true;
            })
            .map((c) => {
                const meta = categoryMeta.get(c.id);
                const slug = meta?.slug;
                if (HIDDEN_KEYS.has(meta?.key)) return null;

                const name = getCategoryName(c);
                if (!name) return null;

                const variantKey = meta?.variant || normalizeNameSlug(name);
                const variant = meta?.isMkgModules && variantKey && styles[`badge--${variantKey}`]
                    ? variantKey
                    : 'default';
                return { name, variant, slug, categoryId: c.id };
            })
            .filter(Boolean)
            .sort((a, b) => (
                String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' })
            ));
    };

    const handleBadgeClick = (event, badge) => {
        event.preventDefault();
        event.stopPropagation();
        if (!badge?.slug || !badge?.categoryId) return;
        if (getSelectedForSlug(badge.slug).has(badge.categoryId)) return;

        doMultipleChanges([
            { fun: 'addParamArrayInt', params: { name: badge.slug, value: badge.categoryId } },
            { fun: 'setParams', params: [{ name: 'page', value: '1' }] },
        ]);
    };

    const badges = getBadges();
    if (badges.length === 0) return null;

    return (
        <div className={styles.badges}>
            {badges.map((b, i) => (
                <button
                    key={`${addOn?.id ?? i}-cat-${b.categoryId ?? i}`}
                    type="button"
                    className={`btn-nostyling ${styles.badge} ${styles[`badge--${b.variant}`]}`}
                    onClick={(event) => handleBadgeClick(event, b)}
                    aria-label={tekst('mkgeu:mkgaddons:badges:aria:addfilter', { name: b.name })}
                >
                    {b.name}
                </button>
            ))}
        </div>
    );
}
